import React, { useState, useEffect } from 'react';
import { Card, Form, Button } from 'react-bootstrap';
import { auth, db } from '../../config/firebaseConfig';
import { 
  collection, 
  addDoc, 
  query, 
  orderBy, 
  onSnapshot,
  serverTimestamp, 
  where,
  getDocs,
  deleteDoc,
  doc
} from 'firebase/firestore';
import moment from 'moment';
import styled from 'styled-components';
import { FaTrash, FaPaperPlane } from 'react-icons/fa';

const NotificationCard = styled(Card)`
  background-color: #f5f7fa;
  border: 1px solid #e2e8f0;
  margin-bottom: 15px;
  border-radius: 10px;
  color: black;
`;

const StyledInput = styled(Form.Control)`
  background-color: #f5f7fa;
  color: black;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 12px;
  width: calc(100% - 20px);
  margin: 0 10px;

  &::placeholder {
    color: #aaa;
    opacity: 1;
  }
`;

const NotificationContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 20px;
`;

const DeleteButton = styled(Button)`
  background: none;
  border: none;
  color: #ff6b6b;
  padding: 5px;
  margin-left: 10px;
  
  &:hover {
    background: rgba(255, 107, 107, 0.1);
    color: #ff8585;
  }
`;

const SendButton = styled(Button)`
  background-color: ${props => props.bgcolor || '#7AB8B5'};
  border: none;
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 10px auto 0;  // Centers the button
  padding: 8px 20px;

  &:hover {
    background-color: ${props => props.bgcolor ? `${props.bgcolor}dd` : '#69a7a4'};
  }
`;

const Notifications = ({ isAdmin }) => {
  const [newNotification, setNewNotification] = useState('');
  const [notifications, setNotifications] = useState([]);
  const [broadcastMessage, setBroadcastMessage] = useState('');

  useEffect(() => {
    // Query for pinned notifications
    const notificationsQuery = query(
      collection(db, 'pinnedNotifications'),
      orderBy('timestamp', 'desc')
    );

    const unsubscribe = onSnapshot(notificationsQuery, (snapshot) => {
      const fetchedNotifications = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setNotifications(fetchedNotifications);
    });

    return () => unsubscribe();
  }, []);

  const handleSendNotification = async (e) => {
    e.preventDefault();
    if (!newNotification.trim()) return;

    try {
      const user = auth.currentUser;
      await addDoc(collection(db, 'pinnedNotifications'), {
        text: newNotification,
        senderEmail: user.email,
        timestamp: serverTimestamp(),
      });

      setNewNotification('');
    } catch (error) {
      console.error('Error sending notification:', error);
    }
  };

  const handleBroadcastMessage = async (e) => {
    e.preventDefault();
    if (!broadcastMessage.trim()) return;

    try {
      const user = auth.currentUser;
      console.log('Current user:', user);
      
      // Get all users
      const allUsersQuery = query(collection(db, 'Employees'));
      const allUsersSnapshot = await getDocs(allUsersQuery);
      console.log('All users found:', allUsersSnapshot.docs.length);
      
      // Filter users who are either not admin or don't have a role
      const nonAdminUsers = allUsersSnapshot.docs.filter(doc => {
        const userData = doc.data();
        return userData.role !== 'admin'; // This will include undefined roles
      });
      
      console.log('Non-admin users found:', nonAdminUsers.length);
      
      // Log each user being messaged
      nonAdminUsers.forEach(doc => {
        const userData = doc.data();
        console.log('Will send message to:', {
          email: userData.eMail,
          role: userData.role
        });
      });
      
      // Send message to each user
      const promises = nonAdminUsers.map(doc => {
        const messageData = {
          senderEmail: user.email,
          receiverEmail: doc.data().eMail,
          text: broadcastMessage,
          timestamp: serverTimestamp(),
          read: false,
          isSystemMessage: true
        };
        console.log('Creating message:', messageData);
        
        return addDoc(collection(db, 'messages'), messageData);
      });

      console.log('Starting to send messages...');
      await Promise.all(promises);
      console.log('All messages sent successfully');
      
      setBroadcastMessage('');
      alert('Toplu mesaj başarıyla gönderildi!');
    } catch (error) {
      console.error('Detailed error in broadcast message:', error);
      alert('Mesaj gönderilirken bir hata oluştu: ' + error.message);
    }
  };

  const handleDeleteNotification = async (notificationId) => {
    try {
      await deleteDoc(doc(db, 'pinnedNotifications', notificationId));
      alert('Bildirim başarıyla silindi!');
    } catch (error) {
      console.error('Error deleting notification:', error);
      alert('Bildirim silinirken bir hata oluştu.');
    }
  };

  return (
    <div>
      {isAdmin && (
        <NotificationContainer>
          <NotificationCard>
            <Card.Body>
              <h5>Yeni Bildirim Oluştur</h5>
              <Form onSubmit={handleSendNotification}>
                <Form.Group>
                  <StyledInput
                    as="textarea"
                    rows={3}
                    value={newNotification}
                    onChange={(e) => setNewNotification(e.target.value)}
                    placeholder="Bildirim mesajını yazın..."
                  />
                </Form.Group>
                <SendButton type="submit" bgcolor="#5A9A9A">
                  <FaPaperPlane /> Bildirim Gönder
                </SendButton>
              </Form>
            </Card.Body>
          </NotificationCard>

          <NotificationCard>
            <Card.Body>
              <h5>Toplu Mesaj Gönder</h5>
              <Form onSubmit={handleBroadcastMessage}>
                <Form.Group>
                  <StyledInput
                    as="textarea"
                    rows={3}
                    value={broadcastMessage}
                    onChange={(e) => setBroadcastMessage(e.target.value)}
                    placeholder="Tüm kullanıcılara gönderilecek mesajı yazın..."
                  />
                </Form.Group>
                <SendButton type="submit" bgcolor="#5A9A9A">
                  <FaPaperPlane /> Toplu Mesaj Gönder
                </SendButton>
              </Form>
            </Card.Body>
          </NotificationCard>
        </NotificationContainer>
      )}

      <div>
        <h5 className="mb-3">Bildirimler</h5>
        {notifications.map((notification) => (
          <NotificationCard key={notification.id}>
            <Card.Body style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <p>{notification.text}</p>
                <small style={{ color: '#aaa' }}>
                  {moment(notification.timestamp?.toDate()).format('DD/MM/YYYY HH:mm')}
                </small>
              </div>
              {isAdmin && (
                <DeleteButton
                  onClick={() => handleDeleteNotification(notification.id)}
                  title="Bildirimi Sil"
                >
                  <FaTrash />
                </DeleteButton>
              )}
            </Card.Body>
          </NotificationCard>
        ))}
      </div>
    </div>
  );
};

export default Notifications;